import { appRoutes } from '@/modules/shared/constants/routes';
import { buildProgramRouteFromRelative } from '@/modules/shared/constants/routes';

export interface DashboardLandingContext {
  programId?: string;
  dashboardKey?: string | null;
  landingRoute?: string | null;
  defaultLandingRoute?: string | null;
  fallbackRoute?: string;
}

export type ProgramWorkspaceKind = 'program' | 'admin' | 'assigned' | 'forms' | 'none';

function normalizeConfiguredRoute(programId: string, route?: string | null): string {
  const trimmed = route?.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('/programs/')) {
    return trimmed;
  }

  return buildProgramRouteFromRelative(programId, trimmed);
}

function extractRelativeDashboardRoute(programId: string, route?: string | null): string {
  const normalized = normalizeConfiguredRoute(programId, route);
  if (!normalized) {
    return '';
  }

  const prefix = `/programs/${programId}/`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length).replace(/^\/+|\/+$/g, '');
  }

  return normalized.replace(/^\/+|\/+$/g, '');
}

export function resolveDashboardLandingPath(context: DashboardLandingContext): string {
  const { programId, dashboardKey, landingRoute, defaultLandingRoute, fallbackRoute } = context;

  if (!programId) {
    return fallbackRoute ?? appRoutes.dashboard();
  }

  const resolvedLandingRoute = normalizeConfiguredRoute(programId, landingRoute);
  if (resolvedLandingRoute) {
    return resolvedLandingRoute;
  }

  const resolvedDefaultRoute = normalizeConfiguredRoute(programId, defaultLandingRoute);
  if (resolvedDefaultRoute) {
    return resolvedDefaultRoute;
  }

  if (dashboardKey && dashboardKey.trim().length > 0) {
    return buildProgramRouteFromRelative(programId, dashboardKey);
  }

  if (fallbackRoute && fallbackRoute.trim().length > 0) {
    return normalizeConfiguredRoute(programId, fallbackRoute);
  }

  return buildProgramRouteFromRelative(programId, 'operational-dashboard');
}

export function getProgramWorkspaceKind(context: Pick<DashboardLandingContext, 'programId' | 'dashboardKey' | 'landingRoute' | 'defaultLandingRoute'>): ProgramWorkspaceKind {
  const { programId, dashboardKey, landingRoute, defaultLandingRoute } = context;
  const route = programId
    ? extractRelativeDashboardRoute(programId, landingRoute ?? defaultLandingRoute ?? dashboardKey ?? undefined)
    : (dashboardKey ?? defaultLandingRoute ?? '').replace(/^\/+|\/+$/g, '');

  switch (route) {
    case 'forms':
      return 'forms';
    case 'admin-dashboard':
      return 'admin';
    case 'workspace':
    case 'operational-dashboard':
      return 'program';
    default:
      return route ? 'assigned' : 'none';
  }
}

export function isConfigurationDashboard(context: Pick<DashboardLandingContext, 'programId' | 'dashboardKey' | 'landingRoute' | 'defaultLandingRoute'>): boolean {
  const kind = getProgramWorkspaceKind(context);
  return kind === 'forms' || kind === 'admin';
}

export function isAuthorizedDashboardRoute(allowedRoute: string | undefined, requestedRoute: string): boolean {
  if (!allowedRoute) {
    return false;
  }

  const normalizedAllowed = allowedRoute.replace(/^\/+|\/+$/g, '').toLowerCase();
  const normalizedRequested = requestedRoute.replace(/^\/+|\/+$/g, '').toLowerCase();

  return normalizedRequested === normalizedAllowed
    || normalizedRequested.startsWith(`${normalizedAllowed}/`);
}